import { forwardRef, type Ref, type ReactNode, type CSSProperties } from 'react';
import { CloseButton } from '../../core/CloseButton/CloseButton.js';
import { Loader } from '../../core/Loader/Loader.js';
import './Notification.css';

export type NotificationVariant = 'default' | 'glass';
export type NotificationColor = 'info' | 'success' | 'warning' | 'error' | string;

export interface NotificationProps {
  title?: ReactNode;
  children?: ReactNode;
  color?: NotificationColor;
  icon?: ReactNode;
  /** Subtle frosted-glass backdrop variant. */
  variant?: NotificationVariant;
  /** Blur amount in px when `variant='glass'`. Default 10. */
  blur?: number;
  /** Render close button. */
  withCloseButton?: boolean;
  onClose?: () => void;
  /** Show a small left border tinted with `color`. Default true. */
  withBorder?: boolean;
  /** Show a spinner instead of the icon (e.g. while a background op runs). */
  loading?: boolean;
  className?: string;
  style?: CSSProperties;
}

/**
 * Toast-style notification card. Distinct from `<Alert>` which is the
 * louder, status-coded banner: notifications are transient, smaller, and
 * stack via the `<NotificationsProvider>`.
 */
export const Notification = forwardRef(function Notification(
  {
    title, children, color = 'info', icon, variant = 'default', blur = 10,
    withCloseButton = true, onClose, withBorder = true, loading,
    className, style
  }: NotificationProps,
  ref: Ref<HTMLDivElement>
): ReactNode {
  const glassStyle = variant === 'glass'
    ? { backdropFilter: `blur(${blur}px) saturate(140%)`, WebkitBackdropFilter: `blur(${blur}px) saturate(140%)` }
    : undefined;
  return (
    <div
      ref={ref}
      role="status"
      aria-live="polite"
      className={[
        'rr-notification',
        `rr-notification--${variant}`,
        `rr-notification--color-${color}`,
        withBorder && 'rr-notification--bordered',
        className
      ].filter(Boolean).join(' ')}
      style={{ ...style, ...glassStyle }}
    >
      <div className="rr-notification__icon">
        {loading ? <Loader size="xs" /> : icon}
      </div>
      <div className="rr-notification__body">
        {title ? <div className="rr-notification__title">{title}</div> : null}
        {children ? <div className="rr-notification__message">{children}</div> : null}
      </div>
      {withCloseButton ? <CloseButton onClick={onClose} className="rr-notification__close" /> : null}
    </div>
  );
});
